import { Injectable, Injector, inject } from "@angular/core";
import {
    Overlay,
    OverlayRef,
    GlobalPositionStrategy,
    OverlayConfig,
} from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { ToastData, ToastPosition, ToastType } from "./toast.types";
import { Toast } from "./toast";

type ToastPositions =
    | "toast-top-left"
    | "toast-top-right"
    | "toast-bottom-left"
    | "toast-bottom-right"
    | "toast-bottom-center";

type ToastColors = "success" | "info" | "warning" | "error";

export type ToastProps = {
    title?: string;
    message: string;
    type: ToastColors;
    positionClass?: ToastPositions;
};

interface ToastInstance {
    id: string;
    overlayRef: OverlayRef;
}

@Injectable({ providedIn: "root" })
export class ToastService {
    private overlay = inject(Overlay);
    private injector = inject(Injector);
    private toasts = new Map<ToastPosition, ToastInstance[]>();
    private toastIdCounter = 0;

    private getPositionStrategy(
        position: ToastPosition,
    ): GlobalPositionStrategy {
        const strategy = this.overlay.position().global();

        switch (position) {
            case "toast-top-left":
                return strategy.top("12px").left("12px");
            case "toast-top-right":
                return strategy.top("12px").right("12px");
            case "toast-top-center":
                return strategy.top("12px").centerHorizontally();
            case "toast-bottom-left":
                return strategy.bottom("12px").left("12px");
            case "toast-bottom-right":
                return strategy.bottom("12px").right("12px");
            case "toast-bottom-center":
            default:
                return strategy.bottom("12px").centerHorizontally();
        }
    }

    private createOverlayConfig(position: ToastPosition): OverlayConfig {
        return new OverlayConfig({
            positionStrategy: this.getPositionStrategy(position),
            hasBackdrop: false,
            panelClass: ["toast-overlay-panel", position],
        });
    }

    private createToast(
        message: string,
        title: string | undefined,
        type: ToastType,
        positionClass: ToastPosition,
    ) {
        const overlayRef = this.overlay.create(
            this.createOverlayConfig(positionClass),
        );
        const toastId = `toast-${++this.toastIdCounter}`;

        const toastData: ToastData = {
            id: toastId,
            title,
            message,
            type,
            timeOut: 5000,
            closeButton: true,
            progressBar: true,
            enableHtml: false,
            titleClass: "toast-title",
            messageClass: "toast-message",
        };

        const portal = new ComponentPortal(Toast, null, this.injector);
        const componentRef = overlayRef.attach<any>(portal);

        componentRef.instance.toastData = toastData;
        componentRef.instance.removed.subscribe((id: string) => {
            this.removeToast(positionClass, id);
        });

        // Track toast instance
        if (!this.toasts.has(positionClass)) {
            this.toasts.set(positionClass, []);
        }
        this.toasts.get(positionClass)!.push({ id: toastId, overlayRef });

        return toastId;
    }

    private removeToast(position: ToastPosition, id: string) {
        const toasts = this.toasts.get(position);
        if (!toasts) return;

        const index = toasts.findIndex((t) => t.id === id);
        if (index === -1) return;

        const toast = toasts[index];

        // Dispose overlay after animation
        setTimeout(() => {
            toast.overlayRef.dispose();
            toasts.splice(index, 1);

            if (toasts.length === 0) {
                this.toasts.delete(position);
            }
        }, 300);
    }

    show(props: ToastProps): void {
        const text = props?.message ?? "";
        const title = props?.title ?? "";
        const type = props?.type ?? "info";
        const positionClass = props?.positionClass ?? "toast-top-right";

        this.createToast(text, title, type, positionClass);
    }

    success(message: string): void;
    success(props: Omit<ToastProps, "type">): void;

    success(props: string | Omit<ToastProps, "type">): void {
        if (typeof props === "string") {
            this.createToast(
                props ?? "",
                undefined,
                "success",
                "toast-bottom-center",
            );
            return;
        }

        const text = props?.message ?? "";
        const title = props?.title ?? "";
        const positionClass = props?.positionClass ?? "toast-top-right";

        this.createToast(text, title, "success", positionClass);
    }

    error(message: string): void;
    error(props: Omit<ToastProps, "type">): void;

    error(props: string | Omit<ToastProps, "type">): void {
        if (typeof props === "string") {
            this.createToast(
                props ?? "",
                undefined,
                "error",
                "toast-bottom-center",
            );
            return;
        }

        const text = props?.message ?? "";
        const title = props?.title ?? "";
        const positionClass = props?.positionClass ?? "toast-top-right";

        this.createToast(text, title, "error", positionClass);
    }

    info(message: string): void;
    info(props: Omit<ToastProps, "type">): void;

    info(props: string | Omit<ToastProps, "type">): void {
        if (typeof props === "string") {
            this.createToast(
                props ?? "",
                undefined,
                "info",
                "toast-bottom-center",
            );
            return;
        }

        const text = props?.message ?? "";
        const title = props?.title ?? "";
        const positionClass = props?.positionClass ?? "toast-top-right";

        this.createToast(text, title, "info", positionClass);
    }

    warning(message: string): void;
    warning(props: Omit<ToastProps, "type">): void;

    warning(props: string | Omit<ToastProps, "type">): void {
        if (typeof props === "string") {
            this.createToast(
                props ?? "",
                undefined,
                "warning",
                "toast-bottom-center",
            );
            return;
        }

        const text = props?.message ?? "";
        const title = props?.title ?? "";
        const positionClass = props?.positionClass ?? "toast-top-right";

        this.createToast(text, title, "warning", positionClass);
    }

    clearAll() {
        this.toasts.forEach((toasts, position) => {
            toasts.forEach((toast) => {
                toast.overlayRef.dispose();
            });
        });
        this.toasts.clear();
    }
}
